import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { Upload, Trash2, Save, Building2, Plus, X, Pencil, Check } from 'lucide-react';

// ── Color picker palette ──────────────────────────────────────────
const TYPE_COLORS = ['#6366f1','#8b5cf6','#ec4899','#ef4444','#f97316','#eab308','#22c55e','#14b8a6','#0ea5e9','#64748b'];

// ── Project Types Manager ─────────────────────────────────────────
const ProjectTypesManager = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // New type form
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeColor, setNewTypeColor] = useState('#6366f1');
  const [adding, setAdding] = useState(false);

  // Editing type name
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [editingTypeName, setEditingTypeName] = useState('');

  // New service per type
  const [newServiceName, setNewServiceName] = useState({});
  const [editingService, setEditingService] = useState(null); // { typeId, serviceId, name }

  const fetchTypes = useCallback(async () => {
    try {
      const r = await axios.get('/api/project-types');
      setTypes(r.data.data);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const addType = async () => {
    if (!newTypeName.trim()) return;
    setAdding(true);
    try {
      const r = await axios.post('/api/project-types', { name: newTypeName.trim(), color: newTypeColor });
      setTypes(prev => [...prev, r.data.data]);
      setNewTypeName('');
      setNewTypeColor('#6366f1');
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
    setAdding(false);
  };

  const saveTypeName = async (type) => {
    if (!editingTypeName.trim()) return;
    try {
      const r = await axios.put(`/api/project-types/${type.id}`, { name: editingTypeName.trim(), color: type.color });
      setTypes(prev => prev.map(t => t.id === type.id ? r.data.data : t));
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
    setEditingTypeId(null);
  };

  const deleteType = async (id) => {
    if (!window.confirm('حذف هذا النوع؟ المشاريع المرتبطة به لن تُحذف.')) return;
    try {
      await axios.delete(`/api/project-types/${id}`);
      setTypes(prev => prev.filter(t => t.id !== id));
    } catch (err) { alert('فشل الحذف'); }
  };

  const addService = async (typeId) => {
    const name = (newServiceName[typeId] || '').trim();
    if (!name) return;
    try {
      const r = await axios.post(`/api/project-types/${typeId}/services`, { name });
      setTypes(prev => prev.map(t => t.id === typeId
        ? { ...t, services: [...(t.services || []), r.data.data] }
        : t
      ));
      setNewServiceName(prev => ({ ...prev, [typeId]: '' }));
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const saveServiceName = async (typeId, service) => {
    if (!editingService?.name?.trim()) return;
    try {
      const r = await axios.put(`/api/project-types/${typeId}/services/${service.id}`, { name: editingService.name.trim() });
      setTypes(prev => prev.map(t => t.id === typeId
        ? { ...t, services: t.services.map(s => s.id === service.id ? r.data.data : s) }
        : t
      ));
    } catch (err) { alert('Error'); }
    setEditingService(null);
  };

  const deleteService = async (typeId, serviceId) => {
    try {
      await axios.delete(`/api/project-types/${typeId}/services/${serviceId}`);
      setTypes(prev => prev.map(t => t.id === typeId
        ? { ...t, services: t.services.filter(s => s.id !== serviceId) }
        : t
      ));
    } catch (err) { alert('فشل الحذف'); }
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>جاري التحميل...</div>;

  return (
    <div>
      {/* Add new type */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <input className="input" style={{ flex: 1, minWidth: 160 }}
          placeholder="اسم النوع (مثال: تصميم جرافيك)"
          value={newTypeName} onChange={e => setNewTypeName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addType()}
        />
        {/* Color picker */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {TYPE_COLORS.map(c => (
            <button key={c} onClick={() => setNewTypeColor(c)}
              style={{ width: 20, height: 20, borderRadius: '50%', background: c, border: newTypeColor === c ? '2px solid var(--text-primary)' : '2px solid transparent', cursor: 'pointer' }}
            />
          ))}
        </div>
        <button className="btn btn-primary btn-sm" onClick={addType} disabled={adding || !newTypeName.trim()}>
          <Plus size={13} /> إضافة نوع
        </button>
      </div>

      {types.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
          لا توجد أنواع مشاريع بعد. أضف النوع الأول!
        </div>
      )}

      {/* Types list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {types.map(type => (
          <div key={type.id} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {/* Type header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: type.color, flexShrink: 0 }} />
              {editingTypeId === type.id ? (
                <>
                  <input className="input" style={{ flex: 1, padding: '4px 8px', fontSize: 13 }}
                    value={editingTypeName}
                    onChange={e => setEditingTypeName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveTypeName(type); if (e.key === 'Escape') setEditingTypeId(null); }}
                    autoFocus
                  />
                  <button className="btn btn-primary btn-sm" onClick={() => saveTypeName(type)}><Check size={12} /></button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingTypeId(null)}><X size={12} /></button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{type.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{type.services?.length || 0} خدمة</span>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditingTypeId(type.id); setEditingTypeName(type.name); }}><Pencil size={12} /></button>
                  <button className="btn btn-secondary btn-sm" onClick={() => deleteType(type.id)} style={{ color: '#ef4444' }}><Trash2 size={12} /></button>
                </>
              )}
            </div>

            {/* Services list */}
            <div style={{ padding: '10px 14px' }}>
              {(type.services || []).map(service => (
                <div key={service.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                  {editingService?.serviceId === service.id ? (
                    <>
                      <input className="input" style={{ flex: 1, padding: '3px 8px', fontSize: 12 }}
                        value={editingService.name}
                        onChange={e => setEditingService(prev => ({ ...prev, name: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') saveServiceName(type.id, service); if (e.key === 'Escape') setEditingService(null); }}
                        autoFocus
                      />
                      <button className="btn btn-primary btn-sm" onClick={() => saveServiceName(type.id, service)}><Check size={11} /></button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditingService(null)}><X size={11} /></button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontSize: 13 }}>{service.name}</span>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}
                        onClick={() => setEditingService({ typeId: type.id, serviceId: service.id, name: service.name })}>
                        <Pencil size={12} />
                      </button>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}
                        onClick={() => deleteService(type.id, service.id)}>
                        <X size={12} />
                      </button>
                    </>
                  )}
                </div>
              ))}

              {/* Add service input */}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <input className="input" style={{ flex: 1, padding: '5px 8px', fontSize: 12 }}
                  placeholder="+ اسم خدمة جديدة"
                  value={newServiceName[type.id] || ''}
                  onChange={e => setNewServiceName(prev => ({ ...prev, [type.id]: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addService(type.id)}
                />
                <button className="btn btn-secondary btn-sm" onClick={() => addService(type.id)}>
                  <Plus size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main Settings page ────────────────────────────────────────────
const Settings = () => {
  const { settings, reload } = useSettings();
  const [systemName, setSystemName] = useState(settings.systemName || 'The Black Div');
  const [systemSubtitle, setSystemSubtitle] = useState(settings.systemSubtitle || 'Client Portal');
  const [quotationTerms, setQuotationTerms] = useState(settings.quotationTerms || '');
  const [quotationValidityDays, setQuotationValidityDays] = useState(settings.quotationValidityDays || '14');
  const [companyEmail, setCompanyEmail] = useState(settings.companyEmail || '');
  const [companyPhone, setCompanyPhone] = useState(settings.companyPhone || '');
  const [companyWebsite, setCompanyWebsite] = useState(settings.companyWebsite || '');
  const [signatureName, setSignatureName] = useState(settings.signatureName || '');
  const [permissions, setPermissions] = useState(() => {
    try { return JSON.parse(settings.permissions || '{}'); } catch (_) { return {}; }
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  // Keep form in sync if settings load after mount
  React.useEffect(() => {
    setSystemName(settings.systemName || 'The Black Div');
    setSystemSubtitle(settings.systemSubtitle || 'Client Portal');
    setQuotationTerms(settings.quotationTerms || '');
    setQuotationValidityDays(settings.quotationValidityDays || '14');
    setCompanyEmail(settings.companyEmail || '');
    setCompanyPhone(settings.companyPhone || '');
    setCompanyWebsite(settings.companyWebsite || '');
    setSignatureName(settings.signatureName || '');
    try { setPermissions(JSON.parse(settings.permissions || '{}')); } catch (_) {}
  }, [settings]);

  const logoSrc = preview || (settings.logoUrl ? settings.logoUrl : null);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put('/api/settings', { systemName, systemSubtitle, quotationTerms, quotationValidityDays, companyEmail, companyPhone, companyWebsite, signatureName, permissions });
      await reload();
      alert('Settings saved!');
    } catch (err) { alert(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleLogoChange = async (file) => {
    if (!file) return;
    // Show local preview instantly
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      await axios.post('/api/settings/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await reload();
      setPreview(null); // use server URL from now on
    } catch (err) { alert(err.response?.data?.message || 'Upload failed'); setPreview(null); }
    finally { setUploading(false); }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Remove the current logo?')) return;
    try {
      await axios.delete('/api/settings/logo');
      setPreview(null);
      await reload();
    } catch (err) { alert('Failed to remove logo'); }
  };

  const togglePerm = (role, key) => setPermissions(p => ({ ...p, [role]: { ...(p[role] || {}), [key]: !(p[role]?.[key]) } }));

  return (
    <div className="page-content" style={{ maxWidth: 680 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>Branding & Settings</h1>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
          Customize how the system looks for everyone
        </p>
      </div>

      {/* Live Preview */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14, letterSpacing: 0.5 }}>
          Live Preview
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border)' }}>
          {/* Logo preview */}
          <div style={{
            width: 42, height: 42, borderRadius: 10, overflow: 'hidden',
            background: logoSrc ? 'transparent' : 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, border: '1px solid var(--border)',
          }}>
            {logoSrc
              ? <img src={logoSrc} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>{(systemName?.[0] || 'B').toUpperCase()}</span>
            }
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{systemName || 'The Black Div'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{systemSubtitle || 'Client Portal'}</div>
          </div>
        </div>
      </div>

      {/* Logo Upload */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>System Logo</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {/* Current logo / placeholder */}
          <div style={{
            width: 80, height: 80, borderRadius: 14, overflow: 'hidden', flexShrink: 0,
            background: logoSrc ? 'transparent' : 'var(--bg-hover)',
            border: '2px dashed var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {logoSrc
              ? <img src={logoSrc} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <Building2 size={28} style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
            }
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Upload a square image for best results. Supports PNG, JPG, SVG � max 5 MB.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => handleLogoChange(e.target.files[0])} />
              <button className="btn btn-primary btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                <Upload size={13} /> {uploading ? 'Uploading...' : 'Upload Logo'}
              </button>
              {(logoSrc) && (
                <button className="btn btn-secondary btn-sm" onClick={handleRemoveLogo} style={{ color: '#ef4444' }}>
                  <Trash2 size={13} /> Remove
                </button>
              )}
            </div>
            {uploading && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--accent)' }}>Uploading logo...</div>
            )}
          </div>
        </div>
      </div>

      {/* System Name & Subtitle */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>System Name</div>
        <form onSubmit={handleSave}>
          <div className="grid-2">
            <div className="input-group">
              <label className="input-label">System Name</label>
              <input className="input" value={systemName} onChange={e => setSystemName(e.target.value)}
                placeholder="The Black Div" maxLength={40} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Shown in sidebar and browser tab
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">Subtitle</label>
              <input className="input" value={systemSubtitle} onChange={e => setSystemSubtitle(e.target.value)}
                placeholder="Client Portal" maxLength={40} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Shown below the name in sidebar
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Quotation Template</div>
        <form onSubmit={handleSave}>
          <div className="grid-2">
            <div className="input-group"><label className="input-label">Company Email</label><input className="input" value={companyEmail} onChange={e => setCompanyEmail(e.target.value)} /></div>
            <div className="input-group"><label className="input-label">Company Phone</label><input className="input" value={companyPhone} onChange={e => setCompanyPhone(e.target.value)} /></div>
            <div className="input-group"><label className="input-label">Company Website</label><input className="input" value={companyWebsite} onChange={e => setCompanyWebsite(e.target.value)} /></div>
            <div className="input-group"><label className="input-label">Validity Days</label><input className="input" type="number" value={quotationValidityDays} onChange={e => setQuotationValidityDays(e.target.value)} /></div>
          </div>
          <div className="input-group"><label className="input-label">Signature Name</label><input className="input" value={signatureName} onChange={e => setSignatureName(e.target.value)} /></div>
          <div className="input-group"><label className="input-label">Default Terms</label><textarea className="input" rows={4} value={quotationTerms} onChange={e => setQuotationTerms(e.target.value)} /></div>
          <button className="btn btn-primary"><Save size={14} /> Save Template</button>
        </form>
      </div>

      <div className="card">
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Role Permissions</div>
        {['manager', 'employee', 'client'].map(role => (
          <div key={role} style={{ borderTop: '1px solid var(--border)', padding: '12px 0' }}>
            <div style={{ fontWeight: 800, textTransform: 'capitalize', marginBottom: 8 }}>{role}</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {['clients', 'services', 'quotations', 'invoices', 'files', 'delete', 'settings'].map(key => (
                <label key={key} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" checked={!!permissions?.[role]?.[key]} onChange={() => togglePerm(role, key)} />
                  {key}
                </label>
              ))}
            </div>
          </div>
        ))}
        <button className="btn btn-primary" onClick={handleSave}><Save size={14} /> Save Permissions</button>
      </div>
    </div>
  );
};

export default Settings;
